import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Bot } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchUserBots, type AdminOwnedBot } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A3.14 — Foydalanuvchi egalik qilgan botlar. GET /admin/users/:id/bots
 * (backend `2c204cf`, read-only bot_platform.bots). Bot @username → bot kartasi.
 */
export function UserBots() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminOwnedBot>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      {
        key: 'username',
        header: t.userExtra.botUsername,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/bots/${r.id}`} className="font-mono text-accent hover:underline">
            @{r.username}
          </Link>
        ),
      },
      { key: 'name', header: t.userExtra.botName, type: 'text', minWidth: 180 },
      {
        key: 'revoked',
        header: t.col.status,
        type: 'enum',
        width: 130,
        accessor: (r) => (r.revoked ? 'revoked' : 'active'),
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'revoked', label: t.userExtra.revoked, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserBots(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.userExtra.botsTitle}
        screenId="A3.14"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminOwnedBot>
        screenId="A3.14"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <Bot size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
